import { Bell, CreditCard, AlertCircle, BanknoteIcon, CheckCheck, Globe, EyeOff, FileText, FileX } from 'lucide-react'
import { useNotifications } from '@/hooks/useNotifications'
import type { AppNotification } from '@/hooks/useNotifications'

const TYPE_ICONS: Record<AppNotification['type'], React.ElementType> = {
  payment_success:       CreditCard,
  payment_failed:        AlertCircle,
  subscription_canceled: BanknoteIcon,
  subscription_updated:  CreditCard,
  site_published:        Globe,
  site_unpublished:      EyeOff,
  page_published:        FileText,
  page_unpublished:      FileX,
}

const TYPE_COLORS: Record<AppNotification['type'], string> = {
  payment_success:       'text-green-600 bg-green-50',
  payment_failed:        'text-red-600 bg-red-50',
  subscription_canceled: 'text-amber-600 bg-amber-50',
  subscription_updated:  'text-violet-600 bg-violet-50',
  site_published:        'text-green-600 bg-green-50',
  site_unpublished:      'text-gray-600 bg-gray-100',
  page_published:        'text-blue-600 bg-blue-50',
  page_unpublished:      'text-gray-600 bg-gray-100',
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

export function NotificationsPage() {
  const { notifications, loading, unreadCount, markRead, markAllRead } = useNotifications()

  return (
    <div className="max-w-2xl mx-auto py-10 px-6">
      <div className="mb-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Bell className="h-6 w-6 text-violet-600" />
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          {unreadCount > 0 && (
            <span className="rounded-full bg-violet-600 px-2 py-0.5 text-xs font-bold text-white">
              {unreadCount}
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={() => { void markAllRead() }}
            className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-violet-600 hover:bg-violet-50 transition-colors"
          >
            <CheckCheck className="h-4 w-4" />
            Mark all as read
          </button>
        )}
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 animate-pulse rounded-xl bg-gray-100" />
          ))}
        </div>
      ) : notifications.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white py-16 text-center">
          <Bell className="h-12 w-12 text-gray-200 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-500">No notifications yet</p>
          <p className="text-xs text-gray-300 mt-1">We'll notify you when sites go live, pages are published, or payments change.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          {notifications.map((n, idx) => {
            const Icon = TYPE_ICONS[n.type]
            const color = TYPE_COLORS[n.type]
            return (
              <div
                key={n.id}
                role="button"
                tabIndex={0}
                aria-label={n.title}
                onClick={() => { if (!n.read) void markRead(n.id) }}
                onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !n.read) { e.preventDefault(); void markRead(n.id) } }}
                className={[
                  'flex gap-4 px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors',
                  idx < notifications.length - 1 && 'border-b border-gray-100',
                  !n.read && 'bg-violet-50/40',
                ].filter(Boolean).join(' ')}
              >
                <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${color}`}>
                  <Icon className="h-4 w-4" />
                </span>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${n.read ? 'text-gray-700' : 'text-gray-900 font-semibold'}`}>{n.title}</p>
                  {n.body && <p className="text-sm text-gray-500 mt-0.5">{n.body}</p>}
                  <p className="text-xs text-gray-400 mt-1">{fmtDate(n.created_at)}</p>
                </div>
                {!n.read && (
                  <div className="flex items-start pt-1.5">
                    <span className="h-2 w-2 rounded-full bg-violet-500 shrink-0" />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
